import { useEffect, useRef, useMemo } from 'react'

import '@hotmart/cosmos/dist/menu-item'

import Icon from '@space-lib/components/icon'

import { CustomColorsUtils } from '@space-lib/utils/colors'
import clsx from 'clsx'
import styled from 'styled-components'

import type { MenuItemProps } from './types'

const Span = styled.span<{ $isMenuActive?: boolean; $textColor: string; $isFixedColor: boolean }>`
  color: ${({ $isFixedColor, $isMenuActive, $textColor }) =>
    $isMenuActive && !$isFixedColor ? $textColor : '_text-gray-900'};
`

const MenuItemIconActive = styled(Icon)<{ $textColor }>`
  color: ${(prop) => prop.$textColor};
`

const MenuItem = ({
  label,
  iconPrimary,
  iconSecondary,
  onClick,
  isActive,
  className,
  customColor
}: MenuItemProps) => {
  const menuRef = useRef<HTMLDivElement>()
  const textColor = CustomColorsUtils.getBestContrastColor(customColor)
  const itemClasses = clsx(
    'hot-application--minimized _cursor-pointer',
    {
      '_d-flex': Boolean(iconSecondary)
    },
    className
  )

  const spanClasses = clsx('_text-2', {
    '_ml-0 _mr-2 _flex-1': Boolean(iconSecondary)
  })

  const isMenuActive = useMemo(() => (isActive ? { active: true } : null), [isActive])

  useEffect(() => {
    if (menuRef.current) {
      const menuItem = menuRef.current.shadowRoot?.querySelector(
        '.hot-application-menu__item'
      ) as HTMLDivElement

      menuItem.setAttribute('part', 'item')
    }
  }, [isMenuActive])

  return (
    <hot-menu-item
      ref={menuRef}
      onClick={onClick}
      data-test='menu-item'
      class={itemClasses}
      {...isMenuActive}>
      {iconPrimary && (
        <div className='hot-application-menu__icon'>
          {isMenuActive && !CustomColorsUtils.isFixedColor(customColor) ? (
            <MenuItemIconActive
              $textColor={textColor}
              icon={iconPrimary}
              data-test='menu-icon-primary'
            />
          ) : (
            <Icon icon={iconPrimary} data-test='menu-icon-primary' />
          )}
        </div>
      )}

      <Span
        className={spanClasses}
        $isFixedColor={Boolean(CustomColorsUtils.isFixedColor(customColor))}
        $textColor={textColor}
        $isMenuActive={Boolean(isMenuActive)}>
        {label}
      </Span>

      {iconSecondary && (
        <div className='hot-application-menu__icon'>
          {isMenuActive ? (
            <MenuItemIconActive
              $textColor={textColor}
              icon={iconSecondary}
              data-test='menu-icon-secondary'
            />
          ) : (
            <Icon icon={iconSecondary} data-test='menu-icon-secondary' className='_text-gray-900' />
          )}
        </div>
      )}
    </hot-menu-item>
  )
}

export default MenuItem
